/**
 * The print colours a Direct Sale text can take — a flex-foil style palette
 * after Spreadshirt. Digital print (DTF/DTG) reproduces any of them, so the
 * palette is a choice of taste, not of machinery; metallic tones are flat
 * colours and named that way.
 *
 * `key` names the colour in `directSale.textColors.*`; the hex is what lands
 * on the Fabric text. Shown sorted by colour (sortByHue): the neutrals from
 * light to dark, then around the colour wheel from red.
 */
const TEXT_COLORS = [
  { key: 'white', hex: '#FFFFFF' },
  { key: 'cream', hex: '#F5EBD7' },
  { key: 'lightGrey', hex: '#D1D5DB' },
  { key: 'silverGrey', hex: '#A8A9AD' },
  { key: 'grey', hex: '#6B7280' },
  { key: 'anthracite', hex: '#374151' },
  { key: 'black', hex: '#111827' },
  { key: 'beige', hex: '#D6BF94' },
  { key: 'brown', hex: '#6B4226' },
  { key: 'goldYellow', hex: '#C9A227' },
  { key: 'neonYellow', hex: '#E6FF00' },
  { key: 'yellow', hex: '#FFD500' },
  { key: 'neonOrange', hex: '#FF6A13' },
  { key: 'orange', hex: '#F58220' },
  { key: 'red', hex: '#EF4444' },
  { key: 'burgundy', hex: '#7A1F2B' },
  { key: 'neonPink', hex: '#FF2E93' },
  { key: 'pink', hex: '#F7A8C4' },
  { key: 'fuchsia', hex: '#C2185B' },
  { key: 'lavender', hex: '#B9A6E0' },
  { key: 'purple', hex: '#8B5CF6' },
  { key: 'aubergine', hex: '#4B2A6E' },
  { key: 'skyBlue', hex: '#8EC9EF' },
  { key: 'turquoise', hex: '#00A7B5' },
  { key: 'royalBlue', hex: '#1D4ED8' },
  { key: 'navy', hex: '#1E3A5F' },
  { key: 'mint', hex: '#9DE0C0' },
  { key: 'neonGreen', hex: '#39FF14' },
  { key: 'green', hex: '#22C55E' },
  { key: 'kellyGreen', hex: '#0F8A3A' },
  { key: 'olive', hex: '#6B6E2F' },
  { key: 'darkGreen', hex: '#1F4D2B' },
] as const;

export type TextColorKey = (typeof TEXT_COLORS)[number]['key'];
export type TextColor = (typeof TEXT_COLORS)[number];

/**
 * Hue (0–360), saturation, lightness and chroma (0–1) of a #rrggbb colour.
 * Chroma tells a neutral apart where saturation cannot: a dark bluish
 * black has a high HSL saturation but hardly any colour in it.
 */
export function hsl(hex: string): {
  h: number;
  s: number;
  l: number;
  c: number;
} {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l, c: 0 };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h =
    max === r
      ? ((g - b) / d) % 6
      : max === g
        ? (b - r) / d + 2
        : (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  return { h, s, l, c: d };
}

/** Greys and near-greys read as "no colour"; they lead the palette. */
const NEUTRAL_CHROMA = 0.12;

/**
 * Picker order: neutrals first, lightest to darkest, then the colours by
 * hue starting at red, a lighter tone before a darker one of the same hue.
 */
export function sortByHue<T extends { hex: string }>(
  colors: readonly T[]
): T[] {
  const rank = (c: T): [number, number, number] => {
    const { h, l, c: chroma } = hsl(c.hex);
    return chroma < NEUTRAL_CHROMA ? [0, 0, -l] : [1, h, -l];
  };
  return [...colors].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    return ra[0] - rb[0] || ra[1] - rb[1] || ra[2] - rb[2];
  });
}

export const TEXT_COLOR_PALETTE: readonly TextColor[] = sortByHue(TEXT_COLORS);

/** Default text colour on light garments. */
export const TEXT_COLOR_DARK = '#111827';
/** Default text colour on dark garments. */
export const TEXT_COLOR_LIGHT = '#FFFFFF';

/** The palette entry for a fill, case-insensitive; null for a foreign one. */
export function findTextColor(
  hex: string | null | undefined
): TextColor | null {
  if (!hex) return null;
  const wanted = hex.toUpperCase();
  return TEXT_COLOR_PALETTE.find((c) => c.hex === wanted) ?? null;
}
